// ============================================================================
// ZONE HEATMAP COMPONENT
// ============================================================================
// Interactive strike zone visualization with 3x3 grid (zones 1-9) + chase zones
// (11-14). Supports multiple metrics: pitch count, whiff rate, hit rate, swing rate.
// Color-coded from cold (blue) to hot (red) based on metric intensity.
// ============================================================================

import React, { useMemo, useState } from 'react';
import { Box, Typography, ToggleButton, ToggleButtonGroup, Tooltip, Chip, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';

// Zone labels matching MLB standard (for RHB / default)
const ZONE_LABELS_RHB = {
  1: 'High\nInside', 2: 'High\nMiddle', 3: 'High\nOutside',
  4: 'Mid\nInside', 5: 'Heart', 6: 'Mid\nOutside',
  7: 'Low\nInside', 8: 'Low\nMiddle', 9: 'Low\nOutside',
  11: 'Above', 12: 'Below', 13: 'Inside', 14: 'Outside'
};
// For LHB, inside/outside flip (left side becomes away, right side becomes arm-side)
const ZONE_LABELS_LHB = {
  1: 'High\nOutside', 2: 'High\nMiddle', 3: 'High\nInside',
  4: 'Mid\nOutside', 5: 'Heart', 6: 'Mid\nInside',
  7: 'Low\nOutside', 8: 'Low\nMiddle', 9: 'Low\nInside',
  11: 'Above', 12: 'Below', 13: 'Outside', 14: 'Inside'
};

// Heatmap color scale (blue → green → yellow → orange → red)
const HEATMAP_COLORS = [
  'rgba(33,  150, 243, 0.15)', // Very cold (near 0)
  'rgba(33,  150, 243, 0.40)', // Cold
  'rgba(76,  175, 80,  0.45)', // Cool
  'rgba(255, 235, 59,  0.55)', // Warm
  'rgba(255, 152, 0,   0.65)', // Hot
  'rgba(244, 67,  54,  0.75)', // Very hot
  'rgba(211, 47,  47,  0.90)', // Extreme
];

function getHeatColor(value, max, inverted = false) {
  if (!max || max === 0) return HEATMAP_COLORS[0];
  let ratio = Math.min(value / max, 1);
  if (inverted) ratio = 1 - ratio;
  const idx = Math.min(Math.floor(ratio * (HEATMAP_COLORS.length - 1)), HEATMAP_COLORS.length - 1);
  return HEATMAP_COLORS[idx];
}

const METRIC_OPTIONS = [
  { key: 'pitches', label: 'Pitches', format: v => v, desc: 'Total pitches seen in zone' },
  { key: 'swingRate', label: 'Swing%', format: v => `${v}%`, desc: 'Swing rate in zone' },
  { key: 'whiffRate', label: 'Whiff%', format: v => `${v}%`, desc: 'Whiff rate when swinging', inverted: true },
  { key: 'hitRate', label: 'Hit%', format: v => `${v}%`, desc: 'Batting average on balls in play' },
];

/**
 * ZoneHeatmap - Interactive MLB strike zone visualization
 * @param {Object} props
 * @param {Array} props.zones - Array of zone objects with stats
 * @param {string} props.title - Title for the heatmap
 * @param {string} props.perspective - 'batter' or 'pitcher' context label
 */
const ZoneHeatmap = ({ zones = [], title = 'Strike Zone Heatmap', perspective = 'batter', playerHand = 'R' }) => {
  const theme = useTheme();
  const [metric, setMetric] = useState('pitches');

  const currentMetricDef = METRIC_OPTIONS.find(m => m.key === metric) || METRIC_OPTIONS[0];
  const ZONE_LABELS = (playerHand || 'R').toUpperCase().startsWith('L') ? ZONE_LABELS_LHB : ZONE_LABELS_RHB;

  // Build zone lookup
  const zoneMap = useMemo(() => {
    const map = {};
    for (const z of zones) {
      map[z.zone] = z;
    }
    return map;
  }, [zones]);

  // Find max value for color scaling
  const maxValue = useMemo(() => {
    let max = 0;
    for (const z of zones) {
      const v = z[metric] || 0;
      if (v > max) max = v;
    }
    return max;
  }, [zones, metric]);

  const renderZoneCell = (zoneNum, width, height, isChase = false) => {
    const zd = zoneMap[zoneNum] || {};
    const value = zd[metric] || 0;
    const pitches = zd.pitches || 0;
    const bgColor = getHeatColor(value, maxValue, currentMetricDef.inverted);
    
    const tooltipContent = (
      <Box sx={{ p: 0.5 }}>
        <Typography variant="subtitle2" fontWeight="bold">{ZONE_LABELS[zoneNum]?.replace('\n', ' ')}</Typography>
        <Typography variant="caption" display="block">Pitches: {pitches}</Typography>
        <Typography variant="caption" display="block">Swings: {zd.swings || 0}</Typography>
        <Typography variant="caption" display="block">Whiffs: {zd.whiffs || 0}</Typography>
        <Typography variant="caption" display="block">In Play: {zd.inPlay || 0}</Typography>
        <Typography variant="caption" display="block">Hits: {zd.hits || 0}</Typography>
        <Typography variant="caption" display="block">Swing%: {zd.swingRate || 0}%</Typography>
        <Typography variant="caption" display="block">Whiff%: {zd.whiffRate || 0}%</Typography>
        <Typography variant="caption" display="block">Hit%: {zd.hitRate || 0}%</Typography>
      </Box>
    );

    return (
      <Tooltip title={tooltipContent} arrow placement="top" key={`zone-${zoneNum}`}>
        <Box
          sx={{
            width, height,
            display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center',
            backgroundColor: bgColor,
            border: isChase ? `1px dashed ${alpha(theme.palette.text.secondary, 0.3)}` : `1px solid ${alpha(theme.palette.divider, 0.5)}`,
            borderRadius: isChase ? '4px' : 0,
            cursor: 'pointer',
            transition: 'all 0.15s ease',
            '&:hover': {
              transform: 'scale(1.05)',
              zIndex: 2,
              boxShadow: `0 0 8px ${alpha(theme.palette.primary.main, 0.4)}`,
            }
          }}
        >
          <Typography variant="caption" fontWeight="bold" sx={{ fontSize: '0.75rem', lineHeight: 1.1, textAlign: 'center', color: theme.palette.text.primary }}>
            {currentMetricDef.format(value)}
          </Typography>
          <Typography variant="caption" sx={{ fontSize: '0.75rem', opacity: 0.7, color: theme.palette.text.secondary }}>
            {pitches}p
          </Typography>
        </Box>
      </Tooltip>
    );
  };

  const cellSize = 64;
  const chaseSize = 40;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        <Typography variant="subtitle1" fontWeight="bold">{title}</Typography>
        <Chip label={perspective === 'pitcher' ? 'Pitcher View' : 'Batter View'} size="small" variant="outlined" />
      </Box>

      {/* Metric selector */}
      <ToggleButtonGroup
        value={metric}
        exclusive
        onChange={(e, v) => v && setMetric(v)}
        size="small"
        sx={{ mb: 2 }}
      >
        {METRIC_OPTIONS.map(m => (
          <ToggleButton key={m.key} value={m.key} sx={{ textTransform: 'none', fontSize: '0.75rem', px: 1.5 }}>
            {m.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>

      {/* Zone visualization */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
        {/* Chase zone: Above */}
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          {renderZoneCell(11, cellSize * 3, chaseSize, true)}
        </Box>

        {/* Main zone + Inside/Outside chase zones */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {/* Chase: Inside */}
          {renderZoneCell(13, chaseSize, cellSize * 3, true)}

          {/* 3x3 Strike Zone Grid */}
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: `repeat(3, ${cellSize}px)`,
            gridTemplateRows: `repeat(3, ${cellSize}px)`,
            border: `2px solid ${theme.palette.text.primary}`,
            borderRadius: '2px',
          }}>
            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(z => renderZoneCell(z, cellSize, cellSize))}
          </Box>

          {/* Chase: Outside */}
          {renderZoneCell(14, chaseSize, cellSize * 3, true)}
        </Box>

        {/* Chase zone: Below */}
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          {renderZoneCell(12, cellSize * 3, chaseSize, true)}
        </Box>

        {/* Legend */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5 }}>
          <Typography variant="caption" sx={{ opacity: 0.7 }}>Low</Typography>
          <Box sx={{ display: 'flex', height: 12, borderRadius: 1, overflow: 'hidden' }}>
            {HEATMAP_COLORS.map((c, i) => (
              <Box key={i} sx={{ width: 20, height: '100%', backgroundColor: c }} />
            ))}
          </Box>
          <Typography variant="caption" sx={{ opacity: 0.7 }}>High</Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default ZoneHeatmap;
